import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShpRowEdit } from '@forest/contracts';
import { cn } from '@/lib/cn';
import type { EditableShpRow } from './useImport';

/** Field keys the step edits, in table order. */
const FIELDS = ['raion', 'primarie', 'codPrimarie', 'anul', 'ucf', 'suprafata', 'entitateSilvica', 'ocolSilvic'] as const;
type FieldKey = (typeof FIELDS)[number];
/** Without these the row is skipped; UCF is highlighted but optional. */
const REQUIRED: readonly FieldKey[] = ['raion', 'primarie', 'anul'];
/** Fields the bulk-fill bar can set on every visible row. */
const BULK: readonly FieldKey[] = ['anul', 'raion', 'primarie', 'codPrimarie', 'entitateSilvica', 'ocolSilvic'];
const NUMERIC: readonly FieldKey[] = ['anul', 'suprafata'];
const PAGE = 50;

/** Text the user typed, per row and field; absent = unchanged. */
export type ShpDraftEdits = Record<number, Partial<Record<FieldKey, string>>>;

function original(r: EditableShpRow, f: FieldKey): string {
  const v = r[f];
  if (v === null || v === undefined) return '';
  return typeof v === 'number' ? (f === 'suprafata' ? String(Number(v.toFixed(4))) : String(v)) : v;
}

function effective(r: EditableShpRow, edits: ShpDraftEdits, f: FieldKey): string {
  const e = edits[r.rawIndex]?.[f];
  return e !== undefined ? e : original(r, f);
}

function validYear(v: string): boolean {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1900 && n <= 2200;
}

/** Fields still missing for a row once the user's edits are applied. */
export function rowGaps(r: EditableShpRow, edits: ShpDraftEdits): { required: FieldKey[]; ucf: boolean } {
  const required = REQUIRED.filter((f) => {
    const v = effective(r, edits, f).trim();
    return f === 'anul' ? !validYear(v) : v === '';
  });
  return { required, ucf: effective(r, edits, 'ucf').trim() === '' };
}

/** The commit body's `edits`: only fields that differ from the parsed row. */
export function toCommitEdits(rows: readonly EditableShpRow[], edits: ShpDraftEdits): Record<string, ShpRowEdit> {
  const out: Record<string, ShpRowEdit> = {};
  for (const r of rows) {
    const e = edits[r.rawIndex];
    if (!e) continue;
    const patch: Record<string, string | number | null> = {};
    for (const f of FIELDS) {
      const v = e[f];
      if (v === undefined || v === original(r, f)) continue;
      const t = v.trim();
      if (NUMERIC.includes(f)) {
        const n = Number(t.replace(',', '.'));
        patch[f] = t === '' ? null : Number.isFinite(n) ? (f === 'anul' ? Math.round(n) : n) : null;
      } else {
        patch[f] = t === '' ? null : t;
      }
    }
    if (Object.keys(patch).length > 0) out[String(r.rawIndex)] = patch as ShpRowEdit;
  }
  return out;
}

/**
 * The shapefile import's editing step: every row the parser could not
 * complete (district, mayoralty or year missing, which blocks the row) or
 * that has no UCF (optional, gets the `-` placeholder) is listed for
 * completion; a toggle opens every row for editing. Edits travel with the
 * commit and the server rebuilds each key from the corrected fields.
 */
export function ShapefileEditStep({
  rows,
  edits,
  onChange,
  truncated,
}: {
  rows: readonly EditableShpRow[];
  edits: ShpDraftEdits;
  onChange: (next: ShpDraftEdits) => void;
  truncated?: boolean;
}) {
  const { t } = useTranslation(['import', 'common']);
  const needsInput = useMemo(() => rows.filter((r) => r.missing.length > 0), [rows]);
  const [onlyMissing, setOnlyMissing] = useState(needsInput.length > 0);
  const [page, setPage] = useState(0);
  const [bulkField, setBulkField] = useState<FieldKey>('anul');
  const [bulkValue, setBulkValue] = useState('');

  const visible = onlyMissing ? needsInput : rows;
  const pages = Math.max(1, Math.ceil(visible.length / PAGE));
  const current = visible.slice(page * PAGE, page * PAGE + PAGE);

  const stats = useMemo(() => {
    let blocked = 0;
    let noUcf = 0;
    for (const r of rows) {
      const g = rowGaps(r, edits);
      if (g.required.length > 0) blocked += 1;
      else if (g.ucf) noUcf += 1;
    }
    return { blocked, noUcf, edited: Object.keys(toCommitEdits(rows, edits)).length };
  }, [rows, edits]);

  const setCell = (rawIndex: number, f: FieldKey, v: string) =>
    onChange({ ...edits, [rawIndex]: { ...edits[rawIndex], [f]: v } });

  const applyBulk = () => {
    const next = { ...edits };
    for (const r of visible) next[r.rawIndex] = { ...next[r.rawIndex], [bulkField]: bulkValue };
    onChange(next);
  };

  const label = (f: FieldKey) =>
    t(`edit.col.${f}`, {
      defaultValue: {
        raion: 'Raion',
        primarie: 'Primăria',
        codPrimarie: 'Cod primărie',
        anul: 'Anul',
        ucf: 'UCF',
        suprafata: 'Suprafață (ha)',
        entitateSilvica: 'Entitate silvică',
        ocolSilvic: 'Ocol silvic',
      }[f],
    });

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-200">
        <p>
          {t('edit.intro', {
            defaultValue:
              'Completați datele lipsă înainte de import. Câmpurile marcate cu * (raion, primăria, anul) sunt obligatorii: rândurile fără ele nu se importă. UCF-ul este recomandat: fără el, poligonul primește UCF-ul provizoriu „-".',
          })}
        </p>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-semibold">
          <span className={stats.blocked > 0 ? 'text-red-700' : 'text-emerald-700'}>
            {t('edit.statBlocked', { defaultValue: 'Cu câmpuri obligatorii lipsă: {{count}}', count: stats.blocked })}
          </span>
          <span className={stats.noUcf > 0 ? 'text-amber-700' : 'text-emerald-700'}>
            {t('edit.statNoUcf', { defaultValue: 'Fără UCF: {{count}}', count: stats.noUcf })}
          </span>
          <span className="text-slate-600">
            {t('edit.statEdited', { defaultValue: 'Rânduri modificate: {{count}}', count: stats.edited })}
          </span>
        </div>
        {truncated ? (
          <p className="mt-1 text-amber-700">
            {t('edit.truncated', { defaultValue: 'Fișierul are prea multe rânduri pentru editare aici; se afișează primele 20.000.' })}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            checked={onlyMissing}
            onChange={(e) => {
              setOnlyMissing(e.target.checked);
              setPage(0);
            }}
          />
          {t('edit.onlyMissing', {
            defaultValue: 'Doar rândurile cu date lipsă ({{count}} din {{total}})',
            count: needsInput.length,
            total: rows.length,
          })}
        </label>
        <div className="flex flex-wrap items-end gap-2 text-xs">
          <span className="text-slate-500">
            {t('edit.bulk', { defaultValue: 'Completează pentru cele {{count}} rânduri afișate:', count: visible.length })}
          </span>
          <select
            value={bulkField}
            onChange={(e) => setBulkField(e.target.value as FieldKey)}
            className="h-7 rounded border border-slate-300 bg-white px-1 dark:border-slate-600 dark:bg-slate-800"
            aria-label={t('edit.bulkField', { defaultValue: 'Câmp' })}
          >
            {BULK.map((f) => (
              <option key={f} value={f}>
                {label(f)}
              </option>
            ))}
          </select>
          <input
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
            className="h-7 w-32 rounded border border-slate-300 bg-white px-2 dark:border-slate-600 dark:bg-slate-800"
            aria-label={t('edit.bulkValue', { defaultValue: 'Valoare' })}
          />
          <button
            type="button"
            onClick={applyBulk}
            disabled={bulkValue.trim() === '' || visible.length === 0}
            className="h-7 rounded bg-slate-700 px-2 font-semibold text-white disabled:opacity-40"
          >
            {t('edit.bulkApply', { defaultValue: 'Aplică' })}
          </button>
          {Object.keys(edits).length > 0 ? (
            <button
              type="button"
              onClick={() => onChange({})}
              className="h-7 rounded px-2 font-semibold text-slate-600 hover:bg-slate-100"
            >
              {t('edit.reset', { defaultValue: 'Anulează modificările' })}
            </button>
          ) : null}
        </div>
      </div>

      <div className="max-h-[480px] overflow-auto rounded-md border border-slate-200 dark:border-slate-700">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] uppercase text-slate-500 dark:bg-slate-800">
            <tr>
              <th className="px-2 py-1.5 text-left font-semibold">#</th>
              {FIELDS.map((f) => (
                <th key={f} className="px-1 py-1.5 text-left font-semibold">
                  {label(f)}
                  {REQUIRED.includes(f) ? <span className="text-red-600"> *</span> : null}
                </th>
              ))}
              <th className="px-2 py-1.5 text-left font-semibold">{t('edit.col.status', { defaultValue: 'Stare' })}</th>
            </tr>
          </thead>
          <tbody>
            {current.map((r) => {
              const gaps = rowGaps(r, edits);
              return (
                <tr key={r.rawIndex} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-2 py-1 font-semibold text-slate-500">{r.rawIndex + 1}</td>
                  {FIELDS.map((f) => {
                    const value = effective(r, edits, f);
                    const changed = edits[r.rawIndex]?.[f] !== undefined && value !== original(r, f);
                    const bad = (gaps.required as readonly string[]).includes(f);
                    const warn = f === 'ucf' && gaps.ucf;
                    return (
                      <td key={f} className="px-1 py-0.5">
                        <input
                          value={value}
                          inputMode={NUMERIC.includes(f) ? 'decimal' : undefined}
                          onChange={(e) => setCell(r.rawIndex, f, e.target.value)}
                          aria-label={`${label(f)} ${r.rawIndex + 1}`}
                          aria-invalid={bad || undefined}
                          placeholder={warn ? t('edit.ucfPlaceholder', { defaultValue: 'ex. 4A' }) : undefined}
                          className={cn(
                            'h-7 w-full min-w-[4.5rem] rounded border px-1.5 dark:bg-slate-800',
                            f === 'anul' || f === 'codPrimarie' || f === 'ucf' ? 'max-w-[5.5rem] font-mono' : '',
                            f === 'suprafata' ? 'max-w-[6rem] text-right tabular-nums' : '',
                            bad
                              ? 'border-red-400 bg-red-50 dark:border-red-700'
                              : warn
                                ? 'border-amber-400 bg-amber-50 dark:border-amber-700'
                                : changed
                                  ? 'border-sky-400 bg-sky-50 dark:border-sky-700'
                                  : 'border-slate-200 bg-white dark:border-slate-700',
                          )}
                        />
                      </td>
                    );
                  })}
                  <td className="whitespace-nowrap px-2 py-1">
                    {gaps.required.length > 0 ? (
                      <span className="font-semibold text-red-700">
                        {t('edit.statusMissing', {
                          defaultValue: 'lipsește: {{fields}}',
                          fields: gaps.required.map(label).join(', '),
                        })}
                      </span>
                    ) : gaps.ucf ? (
                      <span className="text-amber-700">{t('edit.statusNoUcf', { defaultValue: 'fără UCF' })}</span>
                    ) : (
                      <span className="text-emerald-700">{t('edit.statusOk', { defaultValue: 'complet' })}</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {current.length === 0 ? (
              <tr>
                <td colSpan={FIELDS.length + 2} className="px-3 py-6 text-center text-slate-500">
                  {t('edit.empty', { defaultValue: 'Niciun rând cu date lipsă. Debifați filtrul pentru a edita orice rând.' })}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {pages > 1 ? (
        <div className="flex items-center justify-end gap-2 text-xs">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="rounded px-2 py-1 font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            {t('common:pagination.prev', { defaultValue: 'Înapoi' })}
          </button>
          <span className="tabular-nums text-slate-500">
            {page + 1} / {pages}
          </span>
          <button
            type="button"
            disabled={page >= pages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="rounded px-2 py-1 font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            {t('common:pagination.next', { defaultValue: 'Înainte' })}
          </button>
        </div>
      ) : null}
    </div>
  );
}
